import React from 'react'
import { Box, Typography } from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface MetricConfig {
  key: string
  label: string
  color: string
}

interface LocationData {
  id: string
  name: string
  [key: string]: any
}

interface LocationComparisonChartProps {
  locations: LocationData[]
  metrics: MetricConfig[]
  title?: string
}

export default function LocationComparisonChart({
  locations,
  metrics,
  title = 'Sammenligning af lokationer',
}: LocationComparisonChartProps) {
  const chartData = locations.map((loc) => {
    const dataPoint: any = {
      name: loc.name.length > 20 ? loc.name.substring(0, 20) + '...' : loc.name,
      fullName: loc.name,
    }
    metrics.forEach((metric) => {
      dataPoint[metric.label] = loc[metric.key] || 0
    })
    return dataPoint
  })

  if (locations.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: 'background.default',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">
          Ingen lokationsdata tilgængelig
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={100}
            tick={{ fill: '#6b7280', fontSize: 12 }}
          />
          <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return payload[0].payload.fullName
              }
              return label
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: 20 }}
            iconType="circle"
          />
          {metrics.map((metric) => (
            <Bar 
              key={metric.key}
              dataKey={metric.label} 
              fill={metric.color} 
              radius={[4, 4, 0, 0]} 
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
